import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHeader } from '@/components/base/Card';
import { teacherStudents } from '@/mocks/dashboard';

interface StudentMenuItem {
  key: string;
  icon: string;
  labelKey: string;
  descKey: string;
}

const studentMenuItems: StudentMenuItem[] = [
  { key: 'caseHistory', icon: 'ri-folder-history-line', labelKey: 'dashboard.caseHistory', descKey: 'dashboard.caseHistoryDesc' },
  { key: 'assessment', icon: 'ri-clipboard-line', labelKey: 'dashboard.assessment', descKey: 'dashboard.assessmentDesc' },
  { key: 'goalsLessonPlan', icon: 'ri-flag-line', labelKey: 'dashboard.goalsLessonPlan', descKey: 'dashboard.goalsLessonPlanDesc' },
  { key: 'progressReport', icon: 'ri-bar-chart-line', labelKey: 'dashboard.progressReport', descKey: 'dashboard.progressReportDesc' },
  { key: 'documents', icon: 'ri-file-text-line', labelKey: 'dashboard.documents', descKey: 'dashboard.documentsDesc' },
  { key: 'parentDiary', icon: 'ri-book-open-line', labelKey: 'dashboard.parentDiary', descKey: 'dashboard.parentDiaryDesc' },
];

interface SelectedStudent {
  id: string;
  name: string;
  avatar: string;
}

const ITEMS_PER_PAGE = 6;

export default function StudentGrid() {
  const { t } = useTranslation();
  const [selectedStudent, setSelectedStudent] = useState<SelectedStudent | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredStudents = useMemo(
    () =>
      teacherStudents.filter((s) =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [searchQuery],
  );

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const displayCount = Math.min(ITEMS_PER_PAGE, filteredStudents.length - startIndex);

  return (
    <>
      <div className="bg-background-50 rounded-xl border border-background-200 p-5">
        <CardHeader
          title={t('dashboard.myStudents')}
          subtitle={t('dashboard.activeStudents', { count: teacherStudents.length })}
          icon="ri-group-line"
          iconBg="bg-accent-100"
        />

        {/* Search Input */}
        <div className="relative mb-4 mt-1">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <i className="ri-search-line text-base text-foreground-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('dashboard.searchStudents')}
            className="w-full pl-9 pr-3 py-2 text-sm font-label text-foreground-800 bg-background-100 rounded-lg border border-background-200 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
              aria-label="Clear search"
            >
              <i className="ri-close-circle-fill text-base" />
            </button>
          )}
        </div>

        {/* Student Grid or Empty State */}
        {paginatedStudents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100 mb-3">
              <i className="ri-user-search-line text-xl text-foreground-400" />
            </div>
            <p className="text-sm font-label text-foreground-500">{t('dashboard.noStudentsMatch')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {paginatedStudents.map((s) => (
              <div
                key={s.id}
                onClick={() => setSelectedStudent({ id: s.id, name: s.name, avatar: s.avatar })}
                className="group p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-700 font-label flex-shrink-0">
                    {s.avatar}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-label font-medium text-foreground-800 truncate">{s.name}</p>
                    <p className="text-xs text-foreground-400 font-label">{s.age} yrs</p>
                  </div>
                </div>

                <div className="mb-2">
                  <div className="flex items-center justify-between text-[10px] font-label mb-1">
                    <span className="text-foreground-500">{t('dashboard.classProgress')}</span>
                    <span className="font-700 text-foreground-700">{s.progress}%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-background-300 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        s.progress >= 70 ? 'bg-semantic-success' :
                        s.progress >= 40 ? 'bg-primary-500' :
                        'bg-semantic-warning'
                      }`}
                      style={{ width: `${s.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {filteredStudents.length > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-background-200">
            <span className="text-xs font-label text-foreground-500">
              {t('dashboard.pageOf', { current: safePage, total: totalPages })}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage <= 1}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-label font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line text-sm" />
                {t('dashboard.prevPage')}
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-label font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      page === safePage
                        ? 'bg-accent-500 text-background-50 shadow-sm'
                        : 'text-foreground-500 hover:bg-background-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage >= totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-label font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('dashboard.nextPage')}
                <i className="ri-arrow-right-s-line text-sm" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Student Action Menu Modal */}
      {selectedStudent && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
            onClick={() => setSelectedStudent(null)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div className="relative z-10 w-full max-w-lg bg-background-50 rounded-2xl shadow-floating flex flex-col max-h-[90vh] animate-[modalEnter_200ms_cubic-bezier(0.4,0,0.2,1)]">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-background-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-sm font-700 font-label flex-shrink-0">
                {selectedStudent.avatar}
              </div>
              <div className="flex-1">
                <h2 className="font-heading font-700 text-foreground-900 text-lg">
                  {t('dashboard.studentMenuTitle', { name: selectedStudent.name })}
                </h2>
              </div>
              <button
                onClick={() => setSelectedStudent(null)}
                aria-label="Close modal"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Menu Grid */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-2 gap-3">
                {studentMenuItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStudent(null);
                    }}
                    className="flex flex-col items-start gap-2 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-all duration-200 cursor-pointer text-left group/menu hover:-translate-y-0.5"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700 group-hover/menu:bg-accent-500 group-hover/menu:text-background-50 transition-colors duration-200 flex-shrink-0">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <div>
                      <p className="text-sm font-label font-medium text-foreground-800 whitespace-nowrap">
                        {t(item.labelKey)}
                      </p>
                      <p className="text-[11px] text-foreground-400 font-label mt-0.5 leading-snug">
                        {t(item.descKey)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}